/**
 * compareJobNumbers - Array.sort comparator for job numbers that may carry a
 * letter suffix ("3980V" = the VDT half of contract 3980, "3980P" = precast).
 *
 * Ordering is numeric leading digits first, then the whole value, which is what
 * keeps a contract's halves ADJACENT to its base number — the client's actual
 * requirement. This deliberately mirrors the API's ORDER BY (JOB_NUMBER_SORT in
 * mm-production-items.class.ts): server-ordered rows (the daily production
 * report) and client-sorted lists have to read the same way.
 *
 * Not a plain subtraction: Number('3980V') is NaN and Array.sort treats a NaN
 * comparison as 0, which leaves the list in arbitrary order. Not a plain
 * localeCompare with { numeric: true } either — that collates correctly for
 * clean data but disagrees with the server on ties (it puts '3980' before
 * '03980V'; SQL's raw collation does the opposite) and throws outright on a
 * number, which is what a pre-migration API still returns.
 *
 * Values are upper-cased before comparing because the database collation is
 * case-insensitive: the server considers '3980v' and '3980V' the same value, so
 * the sort must not order them as if they were different jobs. Blank/missing
 * job numbers sort last — an unidentifiable row belongs at the bottom of a
 * report, not the top.
 * @param {(string|number|null|undefined)} a first job number
 * @param {(string|number|null|undefined)} b second job number
 * @example
 * ['3981', '3980V', '9', '3980', '3980P'].sort(compareJobNumbers);
 * // Output: ['9', '3980', '3980P', '3980V', '3981']
 * @returns {number} negative, 0 or positive per Array.sort
 */
type JobNumberValue = string | number | null | undefined;

const NUMERIC_PREFIX = /^\d+/;

const compareJobNumbers = (a: JobNumberValue, b: JobNumberValue): number => {
  const left = a == null ? '' : String(a).trim().toUpperCase();
  const right = b == null ? '' : String(b).trim().toUpperCase();

  if (!left || !right) {
    if (left === right) return 0;
    return left ? -1 : 1;
  }

  const leftDigits = left.match(NUMERIC_PREFIX)?.[0];
  const rightDigits = right.match(NUMERIC_PREFIX)?.[0];

  // A value with no leading digits cannot be placed numerically; keep it after
  // everything that can be.
  if (!leftDigits || !rightDigits) {
    if (leftDigits) return -1;
    if (rightDigits) return 1;
    return left.localeCompare(right);
  }

  const numericDiff = Number(leftDigits) - Number(rightDigits);
  if (numericDiff !== 0) return numericDiff;

  return left.localeCompare(right);
};

export default compareJobNumbers;
